import { Injectable } from '@angular/core';
import * as blazeface from '@tensorflow-models/blazeface';
import '@tensorflow/tfjs';
import { AdminPermissionDto } from '../model/admin-permission-dto';

export interface FaceValidationResult {
  valid: boolean;
  message: string;
  // Internal identifier for which check produced this result — several distinct
  // checks share the same candidate-facing message, so this is what makes a
  // failure traceable to its exact source without re-deriving it by hand.
  reasonCode?: string;
}

interface ScreenCircle {
  cx: number;     // circle center X in screen pixels
  cy: number;     // circle center Y in screen pixels
  r: number;      // circle radius in screen pixels
  scaleX: number; // video pixels per screen pixel (X)
  scaleY: number; // video pixels per screen pixel (Y)
}

@Injectable({ providedIn: 'root' })
export class FaceDetectionService {
  private readonly minDetectionProbability = 0.80;
  private model: blazeface.BlazeFaceModel | null = null;
  private modelLoading: Promise<void> | null = null;

  isEnabled(): boolean {
    try {
      const user = JSON.parse(localStorage.getItem('loggedInUser') || 'null');
      return user?.enableFaceDetection !== false;
    } catch {
      return true;
    }
  }

  shouldRestrictOnFaceDetection(adminPermissions?: AdminPermissionDto): boolean {
    try {
      return adminPermissions?.overrideProctoringSetting?.enableFaceDetection !== false;
    } catch {
      return true;
    }
  }

  // Must match capture-layout.svg viewBox and circle dimensions
  private readonly SVG_W = 752;
  private readonly SVG_H = 439;
  private readonly CIRCLE_R = 145; // radius in SVG px, centered at (376, 219.5)

  async loadModel(): Promise<void> {
    if (this.model) return;
    if (this.modelLoading) return this.modelLoading;
    this.modelLoading = blazeface.load({ modelUrl: 'assets/blazeface/model.json' }).then(async m => {
      this.model = m;
      const warmup = document.createElement('canvas');
      warmup.width = 100;
      warmup.height = 100;
      await m.estimateFaces(warmup, false).catch(() => {});
    });
    return this.modelLoading;
  }

  async runSilentInference(videoElement: HTMLVideoElement): Promise<void> {
    if (!this.model || !videoElement.videoWidth) return;
    await this.model.estimateFaces(videoElement, false).catch(() => {});
  }

  // Compute the circle's position and radius in screen pixels, accounting for
  // the SVG overlay using object-cover over the container element.
  // The container (.overflow-hidden) can differ in width from the video element
  // because it is w-full while the video has fixed pixel dimensions.
  private computeScreenCircle(video: HTMLVideoElement): ScreenCircle {
    const displayW = video.clientWidth  || video.offsetWidth  || this.SVG_W;
    const displayH = video.clientHeight || video.offsetHeight || this.SVG_H;
    const videoW   = video.videoWidth   || displayW;
    const videoH   = video.videoHeight  || displayH;

    // The SVG overlay is applied with object-cover on the container, not the video
    // element itself. Use the container's rendered dimensions so the computed circle
    // radius matches what is actually visible on screen at every breakpoint.
    const container = video.closest('.overflow-hidden') as HTMLElement;
    const svgW = container ? (container.clientWidth  || displayW) : displayW;
    const svgH = container ? (container.clientHeight || displayH) : displayH;

    // object-cover scales the SVG to fill the container, maintaining aspect ratio
    const svgScale = Math.max(svgW / this.SVG_W, svgH / this.SVG_H);

    // The SVG circle is centred at (SVG_W/2, SVG_H/2), and object-cover centres the
    // image in the container, so the circle always appears at the container centre.
    // The video element is also flex-centred inside the container, so the circle
    // centre in video-element CSS-pixel coords is always displayW/2, displayH/2.
    return {
      cx:     displayW / 2,
      cy:     displayH / 2,
      r:      this.CIRCLE_R * svgScale,
      scaleX: videoW / displayW,
      scaleY: videoH / displayH,
    };
  }

  // Returns true if the video-space point (vx, vy) lies strictly inside the screen circle.
  private videoPointInCircle(vx: number, vy: number, circle: ScreenCircle): boolean {
    const sx = vx / circle.scaleX;
    const sy = vy / circle.scaleY;
    const dx = sx - circle.cx;
    const dy = sy - circle.cy;
    return dx * dx + dy * dy <= circle.r * circle.r;
  }

  async validateFace(videoElement: HTMLVideoElement): Promise<FaceValidationResult> {
    if (!this.model) {
      try {
        await this.loadModel();
      } catch {
        return { valid: false, message: 'Face detection model failed to load.', reasonCode: 'MODEL_LOAD_FAILED' };
      }
    }

    // Check if video stream is active
    if (!videoElement?.srcObject) {
      return { valid: false, message: 'Camera is blocked or not accessible.', reasonCode: 'CAMERA_NO_STREAM' };
    }
    const stream = videoElement.srcObject as MediaStream;
    const tracks = stream.getVideoTracks();
    if (!tracks.length || tracks[0].readyState !== 'live') {
      return { valid: false, message: 'Camera is blocked or not accessible.', reasonCode: 'CAMERA_TRACK_INACTIVE' };
    }

    let predictions: blazeface.NormalizedFace[];
    try {
      predictions = await this.model!.estimateFaces(videoElement, false);
    } catch {
      return { valid: false, message: 'Unable to process video for face detection.', reasonCode: 'INFERENCE_FAILED' };
    }

    if (!predictions || predictions.length === 0) {
      return { valid: false, message: 'No face detected. Please position your face in front of the camera.', reasonCode: 'NO_FACE_DETECTED' };
    }

    if (predictions.length > 1) {
      return { valid: false, message: 'Multiple faces detected. Only one person should be visible.', reasonCode: 'MULTIPLE_FACES_DETECTED' };
    }

    const face = predictions[0];

    const prob = Array.isArray(face.probability)
      ? (face.probability as number[])[0]
      : (face.probability as number);
    console.log('[FaceDetection] probability:', prob);
    if (prob !== undefined && prob < this.minDetectionProbability) {
      return { valid: false, message: 'Face not clearly detected. Please ensure your face is fully visible.', reasonCode: 'LOW_CONFIDENCE' };
    }

    const validationResult = this.checkFaceQuality(face, videoElement);
    if (!validationResult.valid) {
      return validationResult;
    }

    return { valid: true, message: 'Face detected successfully.', reasonCode: 'OK' };
  }

  private checkFaceQuality(face: blazeface.NormalizedFace, video: HTMLVideoElement): FaceValidationResult {
    const topLeft     = face.topLeft     as [number, number];
    const bottomRight = face.bottomRight as [number, number];

    const faceLeft = topLeft[0];
    const faceTop = topLeft[1];
    const faceRight = bottomRight[0];
    const faceBottom = bottomRight[1];

    const faceWidth = faceRight - faceLeft;
    const faceHeight = faceBottom - faceTop;
    const videoWidth = video.videoWidth || video.clientWidth;
    const videoHeight = video.videoHeight || video.clientHeight;

    console.log('[FaceDetection] box:', { faceLeft, faceTop, faceRight, faceBottom, faceWidth, faceHeight, videoWidth, videoHeight });

    // 1. Landmarks are mandatory
    if (!face.landmarks) {
      console.log('[FaceDetection] FAIL: no landmarks');
      return { valid: false, message: 'Full face not visible. Please show your entire face.', reasonCode: 'NO_LANDMARKS' };
    }
    const landmarks = face.landmarks as number[][];
    if (landmarks.length < 4) {
      console.log('[FaceDetection] FAIL: fewer than 4 landmarks');
      return { valid: false, message: 'Full face not visible. Please show your entire face.', reasonCode: 'INSUFFICIENT_LANDMARKS' };
    }

    const rightEye = landmarks[0];
    const leftEye  = landmarks[1];
    const nose     = landmarks[2];
    const mouth    = landmarks[3];

    // 2. Face must occupy at least 15% of video dimensions (user too far)
    const minFaceRatio = 0.15;
    const widthRatio   = faceWidth  / videoWidth;
    const heightRatio  = faceHeight / videoHeight;
    console.log('[FaceDetection] size check:', { widthRatio, heightRatio });
    if (widthRatio < minFaceRatio || heightRatio < minFaceRatio) {
      return { valid: false, message: 'Face is too far or too small. Please move closer to the camera.', reasonCode: 'FACE_TOO_FAR' };
    }

    // 3. Circle containment — face center AND all facial landmarks must lie inside
    //    the circle guide. We intentionally skip bounding-box corners: BlazeFace boxes
    //    pad generously beyond the visible face (hair, forehead, chin), so corner checks
    //    produce false rejections even when the face is correctly positioned.
    const circle = this.computeScreenCircle(video);
    const faceCenterX = (faceLeft + faceRight)  / 2;
    const faceCenterY = (faceTop  + faceBottom) / 2;

    if (!this.videoPointInCircle(faceCenterX, faceCenterY, circle)) {
      const faceSX = faceCenterX / circle.scaleX;
      const faceSY = faceCenterY / circle.scaleY;
      const threshold = circle.r * 0.25;
      if (faceSY < circle.cy - threshold) return { valid: false, message: 'Move your face down to fit within the circle.', reasonCode: 'FACE_TOO_HIGH' };
      if (faceSY > circle.cy + threshold) return { valid: false, message: 'Move your face up to fit within the circle.', reasonCode: 'FACE_TOO_LOW' };
      if (faceSX < circle.cx - threshold) return { valid: false, message: 'Move your face right to fit within the circle.', reasonCode: 'FACE_TOO_LEFT' };
      if (faceSX > circle.cx + threshold) return { valid: false, message: 'Move your face left to fit within the circle.', reasonCode: 'FACE_TOO_RIGHT' };
      return { valid: false, message: 'Please centre your face within the circle guide.', reasonCode: 'FACE_OFF_CENTER' };
    }

    const landmarkPoints: [number, number][] = [
      [rightEye[0], rightEye[1]],
      [leftEye[0],  leftEye[1]],
      [nose[0],     nose[1]],
      [mouth[0],    mouth[1]],
    ];
    const allLandmarksInCircle = landmarkPoints.every(([x, y]) => this.videoPointInCircle(x, y, circle));
    console.log('[FaceDetection] circle check:', { circle, faceCenterX, faceCenterY, allLandmarksInCircle });
    if (!allLandmarksInCircle) {
      return { valid: false, message: 'Please keep your full face inside the circle guide.', reasonCode: 'LANDMARKS_OUTSIDE_CIRCLE' };
    }

    // 3b. Eye centering margin — landmarks inside the circle is not enough; the face
    //     must not be pushed to the top/bottom edge. Eyes shifted to within the top 25%
    //     of the circle radius (> 0.75r above center) means the face is too high.
    const eyeMidSY = ((rightEye[1] + leftEye[1]) / 2) / circle.scaleY;
    console.log('[FaceDetection] eye centering:', { eyeMidSY, cy: circle.cy, r: circle.r, upperLimit: circle.cy - circle.r * 0.75 });
    if (eyeMidSY < circle.cy - circle.r * 0.75) {
      return { valid: false, message: 'Move your face down to center it within the circle.', reasonCode: 'EYES_TOO_HIGH_IN_CIRCLE' };
    }
    if (eyeMidSY > circle.cy + circle.r * 0.35) {
      return { valid: false, message: 'Move your face up to center it within the circle.', reasonCode: 'EYES_TOO_LOW_IN_CIRCLE' };
    }

    // 4. Vertical geometry: mouth below eyes, nose between
    const eyeMidY = (rightEye[1] + leftEye[1]) / 2;
    console.log('[FaceDetection] vertical geometry:', { eyeMidY, noseY: nose[1], mouthY: mouth[1] });
    if (mouth[1] <= eyeMidY) {
      return { valid: false, message: 'Full face not visible. Please show your entire face.', reasonCode: 'MOUTH_ABOVE_EYES' };
    }
    if (nose[1] <= eyeMidY || nose[1] >= mouth[1]) {
      return { valid: false, message: 'Full face not visible. Please show your entire face.', reasonCode: 'NOSE_MISALIGNED' };
    }
    // Eyes should be in the upper region of the centered circle (≤ 60% of frame height)
    if (eyeMidY / videoHeight > 0.60) {
      return { valid: false, message: 'Move your face up to fit within the guide.', reasonCode: 'EYES_LOW_IN_FRAME' };
    }

    // 5. Eye separation ≥ 20% of face width — rejects hand/object false positives
    const eyeMidX     = (rightEye[0] + leftEye[0]) / 2;
    const eyeDistance  = Math.abs(leftEye[0] - rightEye[0]);
    const eyeSepRatio  = eyeDistance / faceWidth;
    console.log('[FaceDetection] eye-separation:', { eyeSepRatio });
    if (eyeSepRatio < 0.20) {
      return { valid: false, message: 'Eyes not clearly visible. Please keep your face uncovered.', reasonCode: 'EYES_TOO_CLOSE' };
    }

    // 6. Side-face: nose should be near the horizontal midpoint between the eyes
    const offsetRatio = eyeDistance > 0 ? Math.abs(nose[0] - eyeMidX) / eyeDistance : 1;
    console.log('[FaceDetection] side-face:', { offsetRatio });
    if (offsetRatio > 0.35) {
      return { valid: false, message: 'Side face detected. Please face the camera directly.', reasonCode: 'SIDE_FACE' };
    }

    // 7. Head-tilt: both eyes should be at roughly the same height
    const tiltRatio = eyeDistance > 0 ? Math.abs(rightEye[1] - leftEye[1]) / eyeDistance : 1;
    console.log('[FaceDetection] tilt:', { tiltRatio });
    if (tiltRatio > 0.25) {
      return { valid: false, message: 'Please keep your head straight and face the camera.', reasonCode: 'HEAD_TILTED' };
    }

    // 8. Full-face geometry check
    return this.hasFullFaceVisible(face, landmarks);
  }

  private hasFullFaceVisible(face: blazeface.NormalizedFace, landmarks: number[][]): FaceValidationResult {
    const topLeft     = face.topLeft     as [number, number];
    const bottomRight = face.bottomRight as [number, number];
    const faceWidth   = bottomRight[0] - topLeft[0];
    const faceHeight  = bottomRight[1] - topLeft[1];

    const rightEye = landmarks[0];
    const leftEye  = landmarks[1];
    const mouth    = landmarks[3];

    const eyeMidY        = (rightEye[1] + leftEye[1]) / 2;
    const eyeRelativeY   = (eyeMidY  - topLeft[1]) / faceHeight;
    const mouthRelativeY = (mouth[1] - topLeft[1]) / faceHeight;
    const eyeToMouthSpan = mouthRelativeY - eyeRelativeY;

    console.log('[FaceDetection] hasFullFaceVisible:', { eyeRelativeY, mouthRelativeY, eyeToMouthSpan, aspectRatio: faceHeight / faceWidth });

    if (eyeRelativeY > 0.65) {
      return { valid: false, message: 'Full face not visible. Please show your entire face.', reasonCode: 'EYES_LOW_IN_BOX' };
    }
    if (mouthRelativeY < 0.45) {
      return { valid: false, message: 'Full face not visible. Please show your entire face.', reasonCode: 'MOUTH_HIGH_IN_BOX' };
    }
    if (eyeToMouthSpan < 0.30) {
      return { valid: false, message: 'Eyes not clearly visible. Please keep your face uncovered.', reasonCode: 'EYE_MOUTH_SPAN_TOO_SMALL' };
    }
    // aspect-ratio gate (faceHeight/faceWidth < 0.6) removed: measured against real
    // candidate failure reports, valid well-lit centered faces routinely scored
    // 0.54-0.59 here, so it was rejecting good photos rather than catching bad ones.
    // if (faceHeight / faceWidth < 0.6) {
    //   return { valid: false, message: 'Full face not visible. Please show your entire face.' };
    // }
    
    console.log('[FaceDetection] PASS: all checks passed');
    return { valid: true, message: '', reasonCode: 'OK' };
  }
}
